import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModuleLink as Link } from '../../components/ModuleLink';
import { useApiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { safeJson, fmtDate } from '../../utils';
import type {
  StakeholderResponse,
  ChangeRequest,
  ImplementationPlan,
} from '../../types';
import Layout from '../../components/Layout';

export default function PlanSignoff() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') ?? '';
  const cid = searchParams.get('cid') ?? '';
  const api = useApiClient();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<
    'form' | 'done' | 'inactive' | 'confirmed'
  >('form');
  const [, setResponse] = useState<StakeholderResponse | null>(null);
  const [change, setChange] = useState<ChangeRequest | null>(null);
  const [decision, setDecision] = useState('');
  const [concern, setConcern] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!id || !cid) {
      setLoading(false);
      return;
    }
    async function load() {
      try {
        const [respData, changeData] = await Promise.all([
          api.publicGet<StakeholderResponse>(`/responses/${encodeURIComponent(id)}`),
          api.publicGet<ChangeRequest>(`/changes/${encodeURIComponent(cid)}`),
        ]);
        setResponse(respData as StakeholderResponse);
        setChange(changeData as ChangeRequest);
        if ((respData as StakeholderResponse).signoffStatus === 'Signed Off')
          setState('done');
        else if (
          ['Closed', 'Rejected'].includes((changeData as ChangeRequest).status)
        )
          setState('inactive');
      } catch {
        showToast('Failed to load record.', 'error');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, cid]);

  async function submit() {
    const errs: Record<string, string> = {};
    if (!decision) errs.decision = 'Please select a decision.';
    if (decision === 'Raise Concern' && !concern.trim())
      errs.concern = 'Please describe your concern.';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      const payload =
        decision === 'Sign Off'
          ? {
              signoffStatus: 'Signed Off',
              signoffDate: new Date().toISOString(),
            }
          : { signoffStatus: 'Concern Raised', concernDetail: concern.trim() };
      await api.publicPatch(`/responses/${encodeURIComponent(id)}`, payload);
      setState('confirmed');
    } catch {
      showToast('Submission failed.', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  const plan = safeJson<Partial<ImplementationPlan>>(
    change?.implementationPlan,
    {},
  );

  return (
    <Layout maxWidth='form'>
          <nav className='breadcrumb'>
            <Link to='/' className='breadcrumb__link'><i className='fa-solid fa-house' aria-hidden='true' /> Home</Link>
            <span className='breadcrumb__sep'>/</span>
            <span className='breadcrumb__current'>Plan Sign-Off</span>
          </nav>

          {loading && (
          <div className='loading-state'>
            <div className='spinner spinner--lg' />
            <span>Loading…</span>
          </div>
        )}

        {!loading && state === 'done' && (
          <div>
            <div className='state-card'>
              <div style={{ fontSize: '3rem', color: 'var(--accent)' }}>
                <i className='fa-solid fa-circle-check' aria-hidden='true' />
              </div>
              <h1 className='state-card__title'>Already Signed Off</h1>
              <p className='state-card__body'>
                Your sign-off has already been recorded. No further action is
                required.
              </p>
            </div>
            <Link to='/' className='btn btn--secondary' style={{ marginTop: 16 }}>
              <i className='fa-solid fa-house' aria-hidden='true' /> Back to Home
            </Link>
          </div>
        )}

        {!loading && state === 'inactive' && (
          <div>
            <div className='state-card'>
              <div className='state-card__icon'>
                <i className='fa-solid fa-ban' aria-hidden='true' />
              </div>
              <h1 className='state-card__title'>Change No Longer Active</h1>
              <p className='state-card__body'>
                This change is no longer active. Sign-off is not required.
              </p>
            </div>
            <Link to='/' className='btn btn--secondary' style={{ marginTop: 16 }}>
              <i className='fa-solid fa-house' aria-hidden='true' /> Back to Home
            </Link>
          </div>
        )}

        {!loading && state === 'confirmed' && (
          <div>
            <div className='alert alert--success'>
              <span className='alert__icon'>
                <i className='fa-solid fa-circle-check' aria-hidden='true' />
              </span>
              <div className='alert__body'>
                <div className='alert__title'>
                  {decision === 'Sign Off'
                    ? 'Sign-off recorded.'
                    : 'Concern raised.'}
                </div>
                <p>
                  {decision === 'Sign Off'
                    ? 'Thank you. The Change Lead has been notified.'
                    : 'Thank you. Your concern has been recorded and the Change Lead will be in touch.'}
                </p>
              </div>
            </div>
            <Link to='/' className='btn btn--secondary'>
              <i className='fa-solid fa-house' aria-hidden='true' /> Back to Home
            </Link>
          </div>
        )}

        {!loading && state === 'form' && change && (
          <div>
            <div style={{ marginBottom: 4 }}>
              <span className='badge badge--primary'>{change.changeId}</span>
              <h1 style={{ marginTop: 6, fontSize: '1.375rem' }}>
                {change.changeName}
              </h1>
              <p className='text-muted'>
                {change.scope} · Proposed Go-Live:{' '}
                {fmtDate(change.proposedGolive)}
              </p>
            </div>
            <div className='divider' />

            <h2
              style={{
                fontSize: '1rem',
                fontWeight: 700,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <i
                className='fa-solid fa-clipboard-list'
                style={{ color: 'var(--primary)' }}
                aria-hidden='true'
              />{' '}
              Implementation Plan Summary
            </h2>
            <div className='field-display-grid' style={{ marginBottom: 16 }}>
              {[
                ['Approach', plan.approach],
                ['Go-Live Date', fmtDate(plan.goLiveDate)],
                ['Stabilisation', plan.stabilisationPeriod],
                ['Plan Version', plan.planVersion],
              ].map(([l, v]) => (
                <div key={l as string} className='field-display'>
                  <div className='field-display__label'>{l as string}</div>
                  <div className='field-display__value'>{v ?? '—'}</div>
                </div>
              ))}
            </div>
            {[
              ['Key Milestones', plan.milestones],
              ['Training Requirements', plan.trainingRequirements],
              ['Communication Plan', plan.communicationPlan],
              ['Risks & Mitigations', plan.risks],
              ['Resource & Cost', plan.resourceCosts],
            ].map(([l, v]) => (
              <div
                key={l as string}
                className='field-display'
                style={{ marginBottom: 12 }}
              >
                <div className='field-display__label'>{l as string}</div>
                <div className='field-display__value'>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'inherit',
                      fontSize: '0.875rem',
                    }}
                  >
                    {(v as string) ?? '—'}
                  </pre>
                </div>
              </div>
            ))}
            {plan.uploadedFileUrl && (
              <div style={{ marginBottom: 16 }}>
                <a
                  href={plan.uploadedFileUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='btn btn--secondary btn--sm'
                >
                  <i className='fa-solid fa-file-lines' aria-hidden='true' />{' '}
                  View Plan Document
                </a>
              </div>
            )}

            <div className='divider' />
            <h2
              style={{
                fontSize: '1rem',
                fontWeight: 700,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <i
                className='fa-solid fa-signature'
                style={{ color: 'var(--primary)' }}
                aria-hidden='true'
              />{' '}
              Your Sign-Off Decision
            </h2>
            <div className='form-group' style={{ marginBottom: 20 }}>
              <label className='form-label'>
                Sign-Off Decision <span className='required'>*</span>
              </label>
              <div className='pill-group'>
                {['Sign Off', 'Raise Concern'].map((v) => (
                  <button
                    key={v}
                    type='button'
                    className={`pill-option${decision === v ? ' is-selected' : ''}`}
                    onClick={() => {
                      setDecision(v);
                      setErrors({});
                    }}
                  >
                    <i
                      className={`fa-solid ${v === 'Sign Off' ? 'fa-check' : 'fa-triangle-exclamation'}`}
                      aria-hidden='true'
                    />{' '}
                    {v}
                  </button>
                ))}
              </div>
              {errors.decision && (
                <span className='form-error'>
                  <i
                    className='fa-solid fa-circle-exclamation'
                    aria-hidden='true'
                  />{' '}
                  {errors.decision}
                </span>
              )}
            </div>
            {decision === 'Raise Concern' && (
              <div className='form-group' style={{ marginBottom: 20 }}>
                <label className='form-label' htmlFor='concern-detail'>
                  Concern Detail <span className='required'>*</span>
                </label>
                <textarea
                  id='concern-detail'
                  className={`form-control${errors.concern ? ' is-error' : ''}`}
                  rows={4}
                  value={concern}
                  onChange={(e) => setConcern(e.target.value)}
                />
                {errors.concern && (
                  <span className='form-error'>
                    <i
                      className='fa-solid fa-circle-exclamation'
                      aria-hidden='true'
                    />{' '}
                    {errors.concern}
                  </span>
                )}
              </div>
            )}
            <button
              className={`btn btn--primary btn--lg${submitting ? ' is-loading' : ''}`}
              onClick={submit}
              disabled={submitting}
            >
              <i className='fa-solid fa-paper-plane' aria-hidden='true' />{' '}
              Submit
            </button>
          </div>
        )}
    </Layout>
  );
}
